'use client';
import { Card, CardContent } from '@/components/ui/card';
import {
  ChartConfig,
  ChartContainer
} from '@/components/ui/chart';
import { Dot, Line, LineChart, Tooltip as RechartsTooltip, YAxis } from 'recharts';
import {
  PARAMETER_COLORS,
  PARAMETER_ZONE_LABELS,
  ParameterData,
  parameterStatusUtils,
  THRESHOLD_CONSTANTS,
} from '../types/ParameterData';

const getColorForValue = parameterStatusUtils.getColorForValue;

interface ParametersTrendGraphProps {
  values: ParameterData[];
  range: { min: number; max: number };
}

interface LegendZone {
  label: string;
  color: string;
  start: number;
  end: number;
}

interface LegendProps {
  zones: LegendZone[];
  legendZoneWidth?: number;
}

function applyLegendGaps(zones: LegendZone[], gap: number): LegendZone[] {
  const numZones = zones.length;
  const totalGap = gap * (numZones - 1);
  const availableHeight = 100 - totalGap;

  return zones.reduce((acc: LegendZone[], zone, index) => {
    const scaledHeight = (zone.end - zone.start) * (availableHeight / 100);
    const newStart = index === 0 ? 0 : acc[index - 1].end + gap;
    acc.push({
      ...zone,
      start: newStart,
      end: newStart + scaledHeight,
    });
    return acc;
  }, []);
}

function ChartLegend({ zones, legendZoneWidth = 1 }: LegendProps) {
  const adjustedZones = applyLegendGaps(
    zones,
    THRESHOLD_CONSTANTS.LEGEND_GAP_PERCENT,
  );
  return (
    <div className='absolute left-0 top-[24px] h-[calc(100%-48px)] w-24'>
      <div className='relative h-full'>
        {adjustedZones.map((zone, index) => (
          <div
            key={index}
            className='absolute left-0'
            style={{
              top: `${zone.start}%`,
              height: `${zone.end - zone.start}%`,
            }}
          >
            <div
              className={`absolute -left-1 h-full w-${legendZoneWidth} rounded-sm`}
              style={{ background: zone.color }}
            />
            <div className='absolute left-3 top-1/2 -translate-y-1/2 w-48'>
              <span className='text-[8px] md:text-sm'>{zone.label}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ParametersTrendGraph({
  values,
  range,
}: ParametersTrendGraphProps) {
  let computedMin: number, computedMax: number;

  computedMin = range.min;
  computedMax = range.max;

  const extraPadding =
    (computedMax - computedMin) *
    (THRESHOLD_CONSTANTS.EXTRA_PADDING_PERCENTAGE / 100);
  const clipMin = computedMin - extraPadding;
  const clipMax = computedMax + extraPadding;
  const totalRange = clipMax - clipMin;

  const normalize = (value: number) =>
    100 - ((value - clipMin) / totalRange) * 100;

  const lowerWarningThreshold =
    range.min +
    (THRESHOLD_CONSTANTS.WARNING_MARGIN_PERCENTAGE / 100) *
      (range.max - range.min);
  const upperWarningThreshold =
    range.min +
    (1 - THRESHOLD_CONSTANTS.WARNING_MARGIN_PERCENTAGE / 100) *
      (range.max - range.min);

  const legendZones: LegendZone[] = [
    {
      label: PARAMETER_ZONE_LABELS.criticalHigh,
      color: PARAMETER_COLORS.critical,
      start: -0.1,
      end: normalize(range.max),
    },
    {
      label: PARAMETER_ZONE_LABELS.warningHigh,
      color: PARAMETER_COLORS.warning,
      start: normalize(range.max),
      end: normalize(upperWarningThreshold),
    },
    {
      label: PARAMETER_ZONE_LABELS.normal,
      color: PARAMETER_COLORS.normal,
      start: normalize(upperWarningThreshold),
      end: normalize(lowerWarningThreshold),
    },
    {
      label: PARAMETER_ZONE_LABELS.warningLow,
      color: PARAMETER_COLORS.warning,
      start: normalize(lowerWarningThreshold),
      end: normalize(range.min),
    },
    {
      label: PARAMETER_ZONE_LABELS.criticalLow,
      color: PARAMETER_COLORS.critical,
      start: normalize(range.min),
      end: 105,
    },
  ];

  const chartConfig: ChartConfig = {
    value: {
      label: 'Value',
      color: 'var(--chart-1)',
    },
  };

  const chartData = values.map((data) => {
    const value = data.value;
    let adjustedValue = value;
    let isClipped = false;
    if (value < clipMin) {
      adjustedValue = clipMin;
      isClipped = true;
    } else if (value > clipMax) {
      adjustedValue = clipMax;
      isClipped = true;
    }
    return {
      ...data,
      adjustedValue,
      isClipped,
      fill: getColorForValue(value, range.min, range.max),
    };
  });

  return (
    <Card className='py-0 h-full border-none shadow-0'>
      <CardContent className='px-1 py-0 h-full'>
        <div className='relative h-full'>
          <ChartLegend zones={legendZones} />
          <div className='ml-24 h-full'>
            <ChartContainer config={chartConfig} className='h-full w-full'>
              <LineChart
                accessibilityLayer
                data={chartData}
                margin={{ top: 24, left: 24, right: 24, bottom: 24 }}
              >
                <YAxis
                  type='number'
                  domain={[() => clipMin, () => clipMax]}
                  hide
                />
                <RechartsTooltip
                  cursor={false}
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const data = payload[0].payload;
                    return (
                      <div className='rounded-lg border bg-background p-2 shadow-sm'>
                        <div className='grid grid-cols-2 gap-2'>
                          <div className='text-sm font-medium'>Date:</div>
                          <div className='text-sm'>
                            {new Date(data.date).toLocaleDateString()}
                          </div>
                          <div className='text-sm font-medium'>Value:</div>
                          <div className='text-sm'>
                            {data.value} {data.unit}
                            {data.isClipped && (
                              <span className='ml-1 text-xs text-red-500'>
                                (clipped)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
                <Line
                  dataKey='adjustedValue'
                  type='linear'
                  stroke='hsl(var(--parameter-graph-line))'
                  strokeWidth={2}
                  dot={({ payload, ...props }) => (
                    <Dot
                      key={payload.date}
                      r={payload.isClipped ? 7 : 5}
                      cx={props.cx}
                      cy={props.cy}
                      fill={payload.fill}
                      stroke={payload.fill}
                    />
                  )}
                />
              </LineChart>
            </ChartContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
